import React, { useState, useEffect } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useHistory } from 'react-router-dom';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ActivateIcon from '@mui/icons-material/CheckCircle';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData, activateData } from '../../../apiService';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import BillPrint from '../Proforma/Bill';
import EstimatePrint from '../Proforma/Estimate';
import PrintIcon from '@mui/icons-material/Print';
import Info from '@mui/icons-material/Info';
import ReactDOM from 'react-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';

function ProformaIndex() {
    const [data, setData] = useState([]);
    const [deleteItemId, setDeleteItemId] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const history = useHistory(); // Access the navigation history
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [isFocused, setIsFocused] = useState('');
    const { isAuthenticated, companyId, permissions, token, logout } = useAuth();
    const hasCreateMasterPermission = permissions.includes('Create Transaction');
    const hasEditMasterPermission = permissions.includes('Edit Transaction');
    const hasDeleteMasterPermission = permissions.includes('Delete Transaction');
    const branchId = 0;
    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }

        // Token is not expired, fetch data
        setFromDate(getFormattedCurrentDate());
        setToDate(getFormattedCurrentDate());
        fetchTableData();
    }, [isAuthenticated, companyId, branchId, token, logout]);

    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchInput, setShowSearchInput] = useState(false);
    //handle search
    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const toggleSearchInput = () => {
        setShowSearchInput(!showSearchInput);
    };

    const handleFromDateChange = (e) => {
        const selectedDate = e.target.value;
        setFromDate(selectedDate);
    };

    const handleToDateChange = (e) => {
        const selectedDate = e.target.value;
        setToDate(selectedDate);
    };
    //search
    const filteredData = data.filter((item) =>
        Object.values(item).some((value) =>
            String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
    );

    const fetchTableData = async () => {

        setData([]);
        try {
            const response = await fetchData('SaleProformaApi', `/CustomerDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
            if (response.responseCode === '1111') {
                setData(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching Customer Details:', error);
            if (error.response && error.response.status === 401) {
                logout();
                history.replace(`/login?redirect=${encodeURIComponent(window.location.pathname)}`);
            }
        }

    };

    const handleDelete = async () => {
        deleteData('SaleProformaApi', `${deleteItemId}`, token)
            .then((response) => {
                fetchTableData();
                setDeleteItemId(null);
                setConfirmDialogOpen(false);
            })
            .catch((error) => {
                console.error('Error deleting record:', error);
            });
    };

    const handleActivate = (itemId) => {
        activateData('SaleProformaApi', `${itemId}`, token)
            .then(() => {
                fetchTableData();
            })
            .catch((error) => {
                console.error('Error activating record:', error);
            });
    };

    const openConfirmDialog = (itemId) => {
        setDeleteItemId(itemId);
        setConfirmDialogOpen(true);
    };

    const closeConfirmDialog = () => {
        setDeleteItemId(null);
        setConfirmDialogOpen(false);
    };

    function formatDate(dateString) {
        const options = { year: 'numeric', month: '2-digit', day: '2-digit' };
        const formattedDate = new Date(dateString).toLocaleDateString('en-GB', options);
        return formattedDate;
    }

    const handlePrint = async (saleid) => {
        try {
            const response = await fetchData('SaleProformaApi', `/SalePrint/${saleid}/${branchId}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <BillPrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    const handlePrintN = async (saleid) => {
        try {
            const response = await fetchData('SaleProformaApi', `/SalePrint/${saleid}/${branchId}`, token);
            const labelData = response.response_data;
            const labelsToPrint = Array.isArray(labelData) ? labelData : [labelData];

            const iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            document.body.appendChild(iframe);

            const iframeDocument = iframe.contentDocument || iframe.contentWindow.document;
            iframeDocument.write('<html><head><style>@media print { header, footer { display: none !important; } }</style></head><body>');

            ReactDOM.render(
                <EstimatePrint labelData={labelData} onClose={() => document.body.removeChild(iframe)} />,
                iframeDocument.body
            );

            iframeDocument.write('</body></html>');
            iframeDocument.close();

            setTimeout(() => {
                iframe.contentWindow.print();
            }, 1000);

        } catch (error) {
            console.error('Error printing Invoice:', error);
        }
    };

    return (
        <div className="w-full  mx-auto" style={{ width: '95%', marginTop: '10px' }}>

            <div className='w-full mb-2 flex'>
                <h5 className='block uppercase tracking-wide text-black productHead flex-grow linkcolor' style={{ fontFamily: 'Georgia, Cambria, "Times New Roman", Times, serif' }}>Proforma Sale</h5>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px', marginLeft: '20px' }}>
                    <input
                        className="shadow text-gray-700 leading-tight"
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={handleSearch}
                        onFocus={() => setIsFocused(true)}
                        onBlur={() => setIsFocused(false)}
                        style={{
                            width: isFocused ? '300px' : '200px',
                            height: '40px',
                            border: '1px solid #ccc',
                            borderRadius: '15px',
                            padding: '0 20px',
                            transition: 'width 0.4s ease-in-out, border-color 0.3s ease-in-out',
                            outline: 'none',
                            borderColor: isFocused ? 'darkcyan' : '#ccc',
                            boxShadow: isFocused ? '0 4px 8px rgba(0, 0, 0, 0.2)' : 'none',
                        }}
                    />

                    <div style={{ marginRight: '2px', maxWidth: '150px', marginLeft: '10px' }}>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="fromDate">
                            From Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            style={{
                                marginTop: '-10px',
                                width: '100%',
                            }}
                        />
                    </div>

                    <div style={{ marginRight: '2px', maxWidth: '150px', marginLeft: '10px' }}>
                        <label className="block uppercase tracking-wide text-black text-xs font-bold mb-2" htmlFor="toDate">
                            To Date
                        </label>
                        <input
                            className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            style={{
                                marginTop: '-10px',
                                width: '100%',
                            }}
                        />
                    </div>

                    <Button
                        style={{ color: "black", height: '36px', marginLeft: '10px' }}
                        onClick={fetchTableData}
                        className="linkcolor"
                    >
                        <IconButton onClick={fetchTableData}>
                            <RefreshIcon style={{ color: 'brown' }} />
                        </IconButton>
                        <span style={{ color: 'brown', fontWeight: 'bolder', marginTop: '-5px' }}>Refresh</span>
                    </Button>

                    {hasCreateMasterPermission && (
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                            <Button
                                component={Link}
                                to="/ProformaCreate"
                                className="linkcolor"
                                style={{ marginLeft: '8px', color: 'orangered', fontWeight: 'bolder' }}
                            >
                                <AddIcon /> CREATE NEW
                            </Button>
                        </div>
                    )}
                </div>


            </div>

            <div className='w-full mb-2 flex' style={{ maxHeight: '520px', overflowY: 'auto' }}>
                <TableContainer component={Paper}>
                    <Table>
                        {/* <TableHead sx={{ backgroundColor: '#58A5B3' }}> */}
                        <TableHead sx={{ backgroundColor: 'teal' }}>
                            <TableRow>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%' }}>Bill Date</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '10%' }}>Bill Number</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '15%' }}>Customer Name</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '13%' }}>Contact Number</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>Amount</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>CSGT</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>SGST</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '7%', textAlign: 'center' }}>Grand Total</TableCell>
                                <TableCell sx={{ borderBottom: '1px solid #F5F5F5', fontSize: '16px', padding: '7px', color: 'whitesmoke', width: '15%', textAlign: 'center' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredData.map((row) => (
                                <TableRow key={row.SaleId}>
                                    <TableCell sx={{ padding: '7px' }}>{formatDate(row.BillDate)}</TableCell>
                                    <TableCell sx={{ padding: '7px' }}>{row.BillNumber}</TableCell>
                                    <TableCell sx={{ padding: '7px' }}>{row.CustomerName}</TableCell>
                                    <TableCell sx={{ padding: '7px' }}>{row.ContactNumber}</TableCell>
                                    <TableCell sx={{ padding: '7px', textAlign: 'right' }}>{row.TaxableAmount}</TableCell>
                                    <TableCell sx={{ padding: '7px', textAlign: 'right' }}>{row.CGSTAmount}</TableCell>
                                    <TableCell sx={{ padding: '7px', textAlign: 'right' }}>{row.SGSTAmount}</TableCell>
                                    <TableCell sx={{ padding: '7px', textAlign: 'right' }}>{row.GrandTotal}</TableCell>
                                    <TableCell sx={{ padding: '7px', textAlign: 'center' }}>
                                        {hasEditMasterPermission && (
                                            <IconButton component={Link} to={`/ProformaEdit/${row.Id}`} aria-label="edit">
                                                <EditIcon style={{color:'blueviolet'}}/>
                                            </IconButton>
                                        )}
                                        <IconButton onClick={() => handlePrint(row.Id)} aria-label="print">
                                            <PrintIcon style={{color:'teal'}}/>
                                        </IconButton>

                                        <IconButton onClick={() => handlePrintN(row.Id)} aria-label="Estimate">
                                            <ReceiptIcon style={{color:'blue'}}/>
                                        </IconButton>

                                        <IconButton component={Link} to={`/ProformaDetails/${row.Id}`} aria-label="info">
                                            <Info style={{color:'orange'}}/>
                                        </IconButton>
                                        {hasDeleteMasterPermission && (
                                            <>
                                                {row.IsActive ? (
                                                    <IconButton onClick={() => openConfirmDialog(row.Id)} aria-label="delete">
                                                        <DeleteIcon style={{color:'brown'}}/>
                                                    </IconButton>
                                                ) : (
                                                    <IconButton onClick={() => handleActivate(row.Id)} aria-label="activate">
                                                        <ActivateIcon style={{color:'green'}}/>
                                                    </IconButton>
                                                )}
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            <Dialog open={confirmDialogOpen} onClose={closeConfirmDialog}>
                <DialogTitle>Delete Confirmation</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this record?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirmDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleDelete} color="secondary">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>

    );
}


export default ProformaIndex;
